import { DailyActivity } from "@/app/@types";
import { supabase } from "../supabase-client";

export const addActivityToDB = async (
    activityType: 'challenge' | 'prediction' | 'bonus',
    pointsEarned: number,
    activityDate?: string, 
    activityId?: string,
    metadata?: Record<string, any>
  ): Promise<DailyActivity> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }
    
      const activity: Omit<DailyActivity, 'id' | 'created_at' | 'updated_at'> = {
        user_id: user.id,
        activity_date: activityDate || new Date().toISOString().split('T')[0],
        points_earned: pointsEarned,
        activity_type: activityType,
        activity_id: activityId,
        metadata: metadata || {}
      };
    
      const { data, error } = await supabase
        .from('daily_activities')
        .upsert(activity, {
          onConflict: 'user_id,activity_type,activity_id,activity_date',
          ignoreDuplicates: false
        })
        .select()
        .single();
    
      if (error) {
        console.error('Error inserting activity:', error);
        throw new Error(`Database error: ${error.message}`);
      }
    
      console.log('Activity saved to database:', data);
      return data;
    } catch (error) {
      console.error('Failed to add activity to database:', error);
      
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Unexpected error occurred while adding activity');
      }
    }
  };

export const addMultipleActivitiesToDB = async (
    activities: {
      activityType: 'challenge' | 'prediction' | 'bonus';
      pointsEarned: number;
      activityDate?: string;
      activityId?: string;
      metadata?: Record<string, any>;
    }[]
  ): Promise<DailyActivity[]> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }
    
      const activitiesToInsert = activities.map(activity => ({
        user_id: user.id,
        activity_date: activity.activityDate || new Date().toISOString().split('T')[0],
        points_earned: activity.pointsEarned,
        activity_type: activity.activityType,
        activity_id: activity.activityId,
        metadata: activity.metadata || {}
      }));
    
      const { data, error } = await supabase
        .from('daily_activities')
        .upsert(activitiesToInsert, {
          onConflict: 'user_id,activity_type,activity_id,activity_date',
          ignoreDuplicates: false
        })
        .select();
    
      if (error) {
        console.error('Error inserting activities:', error);
        throw new Error(`Database error: ${error.message}`);
      }
    
      console.log('Activities saved to database:', data);
      return data || [];
    } catch (error) {
      console.error('Failed to add multiple activities to database:', error);
    
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Unexpected error occurred while adding multiple activities');
      }
    }
  };

export const addChallengeActivity = async (
    pointsEarned: number,
    challengeId?: string,
    activityDate?: string,
    metadata?: Record<string, any>
  ): Promise<DailyActivity> => {
    try {
      return await addActivityToDB('challenge', pointsEarned, activityDate, challengeId, metadata);
    } catch (error) {
      console.error('Failed to add challenge activity:', error);
      throw error;
    }
  };

export const addPredictionActivity = async (
    pointsEarned: number,
    predictionId?: string,
    activityDate?: string,
    metadata?: Record<string, any>
  ): Promise<DailyActivity> => {
    try {
      return await addActivityToDB('prediction', pointsEarned, activityDate, predictionId, metadata);
    } catch (error) {
      console.error('Failed to add prediction activity:', error);
      throw error;
    }
  };

export const addBonusActivity = async (
    pointsEarned: number,
    bonusId?: string,
    activityDate?: string,
    metadata?: Record<string, any>
  ): Promise<DailyActivity> => {
    try {
      return await addActivityToDB('bonus', pointsEarned, activityDate, bonusId, metadata);
    } catch (error) {
      console.error('Failed to add bonus activity:', error);
      throw error; 
    }
  };